import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TooltipProps } from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { useStore } from '../../store/store';
import type { WikiEntryStatus } from '../../store/docsStore';

type UseWikiHelpTooltipParams = {
  tooltipId: string;
  tooltipPlacement?: TooltipProps['placement'];
};

export function useWikiHelpTooltip({ tooltipId, tooltipPlacement }: UseWikiHelpTooltipParams) {
  const { docsStore } = useStore();
  const theme = useTheme();
  const triggerRef = useRef<HTMLElement | null>(null);
  const [autoPlacement, setAutoPlacement] = useState<TooltipProps['placement']>('top');
  const entryState = docsStore.getEntry(tooltipId);
  const status: WikiEntryStatus = entryState?.status ?? 'idle';

  const computePlacement = useCallback(() => {
    if (tooltipPlacement || typeof window === 'undefined' || !triggerRef.current) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const available = {
      top: rect.top,
      bottom: window.innerHeight - rect.bottom,
      left: rect.left,
      right: window.innerWidth - rect.right,
    };
    const sorted = Object.entries(available).sort(([, a], [, b]) => b - a);
    const bestSide = sorted[0]?.[0] ?? 'top';
    if (bestSide === 'left' || bestSide === 'right' || bestSide === 'bottom') {
      setAutoPlacement(bestSide);
    } else {
      setAutoPlacement('top');
    }
  }, [tooltipPlacement]);

  useLayoutEffect(() => {
    if (tooltipPlacement) {
      return;
    }
    const handle = () => computePlacement();
    handle();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [computePlacement, tooltipPlacement]);

  useEffect(() => {
    const abortController = new AbortController();
    docsStore
      .load(tooltipId, { signal: abortController.signal })
      .catch(() => {
        // Suppress unhandled rejection warnings; store tracks error state.
      });
    return () => abortController.abort();
  }, [docsStore, tooltipId]);

  const contentText = entryState?.data?.contentText?.trim();
  const contentImage = entryState?.data?.contentImage ?? undefined;
  const entryUrl = entryState?.data?.url;

  const tooltipContent = useMemo(() => {
    if (!contentText && !contentImage) {
      return null;
    }
    return (
      <div style={{ display: 'block', padding: '10px 14px', boxSizing: 'border-box', maxHeight: 320, overflowY: 'auto' }}>
        {contentImage && (
          <div style={{ width: '100%', marginBottom: contentText ? 8 : 0 }}>
            <img src={contentImage} alt='' style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 4 }} loading='lazy' />
          </div>
        )}
        {contentText ? (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
            }}
          >
            {contentText}
          </p>
        ) : null}
        {entryUrl ? (
          <a
            href={entryUrl}
            target='_blank'
            rel='noopener noreferrer'
            style={{
              display: 'inline-block',
              marginTop: 12,
              fontSize: 13,
              fontWeight: 600,
              color: '#1976d2',
              textDecoration: 'none',
            }}
          >
            Read more...
          </a>
        ) : null}
      </div>
    );
  }, [contentText, contentImage, entryUrl]);

  useEffect(() => {
    if (!contentImage) {
      return;
    }
    const preloader = new Image();
    preloader.src = contentImage;
    return () => {
      preloader.onload = null;
      preloader.onerror = null;
    };
  }, [contentImage]);

  const tooltipBackgroundColor = theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper;

  const tooltipSlotProps = useMemo(() => {
    return {
      popper: {
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 16 } },
          { name: 'flip', options: { fallbackPlacements: ['top', 'bottom', 'right', 'left'] } },
          { name: 'computeStyles', options: { adaptive: false } },
        ],
      },
      tooltip: {
        sx: {
          maxWidth: 240,
          padding: '8px',
          backgroundColor: tooltipBackgroundColor,
        },
      },
      arrow: {
        sx: {
          color: tooltipBackgroundColor,
        },
      },
    } as TooltipProps['slotProps'];
  }, [tooltipBackgroundColor]);

  const resolvedPlacement = tooltipPlacement ?? autoPlacement;

  return {
    status,
    entryState,
    tooltipContent,
    tooltipSlotProps,
    triggerRef,
    resolvedPlacement,
  };
}
